import { 
  getData, 
  postData, 
  putData, 
  patchData, 
  deleteData 
} from '../api/api';
import {
  IncidentDetailsResponse,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  AddCommentRequest,
  UpdateStatusRequest,
  AssignToUserRequest,
  AssignToGroupRequest,
  WhatsAppNotificationResponse,
  EmailNotificationResponse
} from '../types/incident';
import { toIncidentPriorityEnum } from '../lib/incident/priority';
import { buildReadQueryString } from '../lib/api/readQuery';

const INCIDENT_BASE_URL = '/incident';

export const incidentService = {
  // Get all incidents for the current user
  getIncidents: async (
    page: number = 0,
    size: number = 10,
    search?: string,
    fields?: string | string[],
    sort: string = "createdDate,desc",
  ) => {
    return getData(
      `${INCIDENT_BASE_URL}/read?${buildReadQueryString({
        page,
        size,
        sort,
        search,
        fields,
      })}`,
    );
  },

  // Get specific incident by ID (list / legacy read)
  getIncident: async (id: string) => {
    return getData(`${INCIDENT_BASE_URL}/read/${id}`);
  },

  /**
   * Incident detail view: customer + source system summary (OpenAPI: GET /incident/{id}/details).
   */
  getIncidentDetails: async (id: string): Promise<IncidentDetailsResponse> => {
    return getData(`${INCIDENT_BASE_URL}/${id}/details`);
  },

  // Create new incident
  createIncident: async (data: CreateIncidentRequest) => {
    return postData(`${INCIDENT_BASE_URL}/create`, {
      ...data,
      priority: toIncidentPriorityEnum(data.priority),
    });
  },

  // Update incident
  updateIncident: async (data: UpdateIncidentRequest) => {
    return patchData(`${INCIDENT_BASE_URL}/update`, {
      ...data,
      priority: toIncidentPriorityEnum(data.priority),
    });
  },

  // Delete incident
  deleteIncident: async (id: string) => {
    return deleteData(`${INCIDENT_BASE_URL}/delete/${id}`);
  },

  // Assign incident to current user
  assignToMe: async (id: string) => {
    return patchData(`${INCIDENT_BASE_URL}/assign-to-me/${id}`, {});
  },

  // Assign incident to group
  assignToGroup: async (id: string, data: AssignToGroupRequest, groupName?: string) => {
    const url = groupName 
      ? `${INCIDENT_BASE_URL}/assign-to-group/${id}?groupName=${encodeURIComponent(groupName)}`
      : `${INCIDENT_BASE_URL}/assign-to-group/${id}`;
    return patchData(url, data);
  },

  // Add comment to incident
  addComment: async (id: string, data: AddCommentRequest) => {
    return postData(`${INCIDENT_BASE_URL}/${id}/comment`, data);
  },

  // Update incident status
  updateStatus: async (id: string, data: UpdateStatusRequest, statusParam?: string) => {
    const url = statusParam 
      ? `${INCIDENT_BASE_URL}/${id}/status?status=${statusParam}`
      : `${INCIDENT_BASE_URL}/${id}/status`;
    return patchData(url, data);
  },

  sendWhatsAppNotification: async (id: string): Promise<WhatsAppNotificationResponse> => {
    return postData(`${INCIDENT_BASE_URL}/${id}/whatsapp-notification`, {});
  },

  sendEmailNotification: async (id: string): Promise<EmailNotificationResponse> => {
    return postData(`${INCIDENT_BASE_URL}/${id}/email-notification`, {});
  }
};
